import type { Contract, ContractMode, CurrentUpsellPlan } from '@modules/husleie-framework/src/types/Contract.entity';
import type { RentalAdjustmentType, Tier } from '@modules/husleie-framework/src/types/ContractRentalAdjustment.entity';
import type { Deposit, DepositSolution } from '@modules/husleie-framework/src/types/Deposit.entity';
import { PreviousTenant } from '@modules/husleie-framework/src/types/PreviousTenant.entity';
import { PrivateLandlord } from '@modules/husleie-framework/src/types/PrivateLandlord.entity';
import { ProfessionalLandlord } from '@modules/husleie-framework/src/types/ProfessionalLandlord.entity';
import {
  BankAccount,
  LimitedRentDurationType,
  Rent,
  RentDurationEnum,
  RentDurationType,
} from '@modules/husleie-framework/src/types/Rent.entity';
import type RentalCalculation from '@modules/husleie-framework/src/types/RentalCalculation.entity';
import type { RentalObject } from '@modules/husleie-framework/src/types/RentalObject.entity';
import type { SignMethodType, SigningStatus } from '@modules/husleie-framework/src/types/Signing.entity';
import type { Tenant } from '@modules/husleie-framework/src/types/Tenant.entity';
import { UserRoleEnum } from '@modules/husleie-framework/src/types/User.entity';
import type ContractState from '@modules/store/contract/types/ContractState.entity';
import { merge, cloneDeep } from 'lodash';
import Vue from 'vue';
import { MutationTree } from 'vuex';

export const mutations: MutationTree<ContractState> = {
  setLoading: (state: ContractState, payload: boolean) => {
    state.loading = payload;
  },
  setApiError: (state: ContractState, payload: boolean) => {
    state.apiError = payload;
  },
  setFromSignup: (state: ContractState, payload: boolean) => {
    state.fromSignup = payload;
  },
  setContract: (state: ContractState, payload: Contract) => {
    state.contract = cloneDeep(merge(state.contract, payload));
    state.loaded = true;
  },
  setContractStatus: (state: ContractState, payload: SigningStatus) => {
    state.contract.status = payload;
  },
  setContractMode: (state, payload: ContractMode) => {
    state.contract.mode = payload;
  },
  setContractLandlord: (state, payload: ProfessionalLandlord | PrivateLandlord) => {
    state.contract.landlord = payload;
  },
  setContractTenant: (state: ContractState, payload: Tenant) => {
    state.contract.tenant = payload;
  },
  setContractCoTenants: (state: ContractState, payload: Tenant[]) => {
    state.contract.coTenants = payload;
  },
  setContractCoTenant: (state: ContractState, payload: { index: number; data: Tenant }) => {
    state.contract.coTenants.splice(payload.index, 1, payload.data);
  },
  addNewContractCoTenant: (state: ContractState) => {
    const newCoTenant: Tenant = {
      user: {
        id: null,
        firstName: '',
        lastName: '',
        fullName: '',
        role: UserRoleEnum.TENANT,
      },
    };
    state.contract.coTenants.push(newCoTenant);
  },
  deleteContractCoTenant: (state: ContractState, index: number) => {
    state.contract.coTenants.splice(index, 1);
  },

  setContractRentalObject: (state: ContractState, payload: RentalObject) => {
    state.contract.rentalObject = payload;
  },

  setDepositSolution: (state: ContractState, payload: { index: number; data: DepositSolution }) => {
    state.contract.deposit.depositSolutions?.splice(payload.index, 1, payload.data);
  },

  setDepositSolutions: (state: ContractState, payload: DepositSolution[]) => {
    state.contract.deposit.depositSolutions = payload;
  },

  /**
   * Rental Adjustment
   */

  setContractRentalAdjustmentRentalAdjustment: (state: ContractState, payload: RentalAdjustmentType) => {
    state.contract.rentalAdjustment.rentalAdjustment = payload;
  },
  setContractRentalAdjustmentTiers: (state: ContractState, payload: Tier[]) => {
    state.contract.rentalAdjustment.tiers = payload;
  },
  setContractRentalAdjustmentTier: (state: ContractState, payload: { index: number; data: Tier }) => {
    state.contract.rentalAdjustment.tiers.splice(payload.index, 1, payload.data);
  },
  addNewContractRentalAdjustmentTier: (state: ContractState) => {
    const newTier: Tier = {
      date: null,
      rent: null,
    };
    state.contract.rentalAdjustment.tiers.push(newTier);
  },
  deleteContractRentalAdjustmentTier: (state: ContractState, index: number) => {
    state.contract.rentalAdjustment.tiers.splice(index, 1);
  },
  setContractRentalAdjustmentRentRegulation: (state: ContractState, payload: boolean) => {
    state.contract.rentalAdjustment.rentRegulation = payload;
  },
  setContractRentalAdjustmentFirstUsedAndRented: (state: ContractState, payload: boolean) => {
    state.contract.rentalAdjustment.firstUsedAndRented = payload;
  },
  setContractRentalAdjustmentRenovationsLastThreeYears: (state: ContractState, payload: boolean) => {
    state.contract.rentalAdjustment.renovationsLastThreeYears = payload;
  },
  setContractRentalAdjustmentOccupancyAfterRenovations: (state: ContractState, payload: boolean) => {
    state.contract.rentalAdjustment.occupancyAfterRenovations = payload;
  },

  /**
   * Sign
   */
  setContractSign: (state: ContractState, payload: SignMethodType) => {
    Vue.set(state.contract, 'sign', payload);
  },

  /* Rent */
  setContractRent: (state: ContractState, payload: Rent) => {
    Vue.set(state.contract, 'rent', { ...state.contract.rent, ...payload });
  },

  setContractRentDurationType: (state: ContractState, payload: RentDurationType) => {
    const rent: Rent = state.contract.rent
      ? { ...state.contract.rent, durationType: payload }
      : { durationType: payload };
    Vue.set(state.contract, 'rent', rent);
  },
  setContractRentLimitedDurationType: (state: ContractState, payload: LimitedRentDurationType) => {
    if (!state.contract.rent) {
      const rent: Rent = {
        durationType: RentDurationEnum.LIMITED,
        limitedRentDurationType: payload,
      };
      Vue.set(state.contract, 'rent', rent);
    } else {
      state.contract.rent.limitedRentDurationType = payload;
    }
  },
  setContractRentLimitedRentReason: (state: ContractState, payload: string) => {
    const rent: Rent = state.contract.rent
      ? { ...state.contract.rent, limitedRentReason: payload }
      : { limitedRentReason: payload };
    Vue.set(state.contract, 'rent', rent);
  },
  setContractRentStartDate: (state: ContractState, payload: string) => {
    const rent: Rent = state.contract.rent ? { ...state.contract.rent, startDate: payload } : { startDate: payload };
    Vue.set(state.contract, 'rent', rent);
  },
  setContractRentEndDate: (state: ContractState, payload: string) => {
    const rent: Rent = state.contract.rent ? { ...state.contract.rent, endDate: payload } : { endDate: payload };
    Vue.set(state.contract, 'rent', rent);
  },
  setContractRentBindingPeriod: (state: ContractState, payload: number) => {
    const rent: Rent = state.contract.rent
      ? { ...state.contract.rent, bindingPeriod: payload }
      : { bindingPeriod: payload };
    Vue.set(state.contract, 'rent', rent);
  },
  setContractRentNoticePeriod: (state: ContractState, payload: number) => {
    const rent: Rent = state.contract.rent
      ? { ...state.contract.rent, noticePeriod: payload }
      : { noticePeriod: payload };
    Vue.set(state.contract, 'rent', rent);
  },
  setContractDeposit: (state: ContractState, payload: Deposit) => {
    state.contract.deposit = payload;
  },
  setContractRentPaymentDay: (state: ContractState, payload: number) => {
    const rent: Rent = state.contract.rent ? { ...state.contract.rent, paymentDay: payload } : { paymentDay: payload };
    Vue.set(state.contract, 'rent', rent);
  },
  setContractRentLandlordBankAccount: (state: ContractState, payload: BankAccount) => {
    const landlord: Rent['landlord'] = state.contract.rent?.landlord
      ? { ...state.contract.rent.landlord, bankAccount: payload }
      : { bankAccount: payload };
    const rent: Rent = state.contract.rent ? { ...state.contract.rent, landlord } : { landlord };
    Vue.set(state.contract, 'rent', rent);
  },

  // RentalCalculation
  setContractRentalCalculation: (state: ContractState, payload: RentalCalculation) => {
    Vue.set(state.contract, 'rentalCalculation', { ...state.contract.rentalCalculation, ...payload });
  },

  // Previous Tenant
  setContractPreviousTenant: (state: ContractState, payload: PreviousTenant) => {
    Vue.set(state.contract, 'previousTenant', payload);
  },

  setContractPreview: (state: ContractState, payload: { html: any; pdf: any }) => {
    const html: any = payload.html !== undefined ? payload.html : state.contract.preview?.html;
    const pdf: any = payload.pdf !== undefined ? payload.pdf : state.contract.preview?.pdf;
    state.contract.preview = { html, pdf };
  },
  setContractCurrentUpsellPlan: (state: ContractState, payload: CurrentUpsellPlan): void => {
    state.contract.currentUpsellPlan = payload;
  },
};
